import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { orderProductLabel } from '../../../logic/devices';
import OrderStatusBadge, { formatDate, isAddressError } from './orderUi';

export default function OrderDetailModal({ order, onClose }) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => { window.removeEventListener('keydown', onKey); document.body.style.overflow = ''; };
  }, [onClose]);

  if (!order) return null;
  const productNames = (order.products ?? []).map((p) => orderProductLabel(p)).join(', ');
  const meta = [order.order_reference ? `Ref ${order.order_reference}` : '', order.created ? formatDate(order.created) : ''].filter(Boolean).join(' · ');
  const addrErr = isAddressError(order.status);
  const a = order.address || {};
  const cityLine = [a.zip, a.city].filter(Boolean).join(' ');
  const regionLine = [a.state, a.country].filter(Boolean).join(', ');

  return createPortal((
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 460 }}>
        <button className="modal-close" aria-label="Close" onClick={onClose}>×</button>
        <div className="modal-header">
          <h3 style={{ textTransform: 'none' }}>Order details</h3>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 16 }}>
          <OrderStatusBadge status={order.status} />
          <div>
            <div className="body2" style={{ fontWeight: 600 }}>{productNames || 'Device order'}</div>
            {meta && <div className="detail1 text-secondary">{meta}</div>}
          </div>
        </div>

        {addrErr && (
          <div role="note" style={{ display: 'flex', alignItems: 'flex-start', gap: 8, padding: '10px 12px', background: 'var(--wi-status-bad-10)', borderRadius: 8, marginBottom: 16 }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--wi-status-bad)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" style={{ flexShrink: 0, marginTop: 1 }}>
              <circle cx="12" cy="12" r="10" /><path d="M12 8v5" /><path d="M12 16h.01" />
            </svg>
            <span className="detail1" style={{ color: 'var(--wi-status-bad)', lineHeight: 1.5 }}>
              The shipping address couldn&apos;t be validated, so this order won&apos;t ship. Order the device again with a corrected address.
            </span>
          </div>
        )}

        {(a.address1 || a.name) && (
          <>
            <span className="eyebrow text-secondary" style={{ display: 'block', marginBottom: 6 }}>Shipping address</span>
            <div style={{ background: 'var(--wi-background-secondary)', border: '1px solid var(--wi-action-primary)', borderRadius: 8, padding: '12px 14px', lineHeight: 1.6, marginBottom: 16 }}>
              {a.name && <div className="body2" style={{ fontWeight: 600 }}>{a.name}</div>}
              {a.email && <div className="detail1 text-secondary">{a.email}</div>}
              {a.address1 && <div className="body2">{a.address1}{a.address2 ? `, ${a.address2}` : ''}</div>}
              {cityLine && <div className="body2">{cityLine}</div>}
              {regionLine && <div className="body2">{regionLine}</div>}
            </div>
          </>
        )}

        {(order.tracking_number || order.tracking_url) && (
          <>
            <span className="eyebrow text-secondary" style={{ display: 'block', marginBottom: 6 }}>Tracking</span>
            <div className="body2" style={{ marginBottom: 16 }}>
              {order.tracking_number || '—'}
              {order.tracking_url && (
                <a href={order.tracking_url} target="_blank" rel="noreferrer" style={{ marginLeft: 8, color: 'var(--wi-action-primary)', fontWeight: 600 }}>Track →</a>
              )}
            </div>
          </>
        )}

        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 8 }}>
          <button type="button" className="btn-primary" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  ), document.body);
}
